/**
 * NotificationPreferencesSection - Toggles for push/email/queue notification preferences
 * Self-contained: owns its preferences state and Firestore update handler
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc } from 'firebase/firestore';
import { User as FirebaseUser } from 'firebase/auth';
import { db } from '@/lib/firebase';
import { toast } from '@/lib/toast';
import { User } from '@/types';

interface NotificationPreferencesSectionProps {
  user: User;
  firebaseUser: FirebaseUser | null;
}

export default function NotificationPreferencesSection({ user, firebaseUser }: NotificationPreferencesSectionProps) {
  const { t } = useTranslation();
  const [savingNotifications, setSavingNotifications] = useState(false);

  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    enablePushNotifications: user?.enablePushNotifications ?? true,
    enableEmailNotifications: user?.enableEmailNotifications ?? true,
    notifyOnPositionUpdate: user?.notifyOnPositionUpdate ?? true,
    notifyOnYourTurn: user?.notifyOnYourTurn ?? true,
    notifyOnCancellation: user?.notifyOnCancellation ?? true,
  });

  const handleToggleNotification = async (key: keyof typeof notificationPrefs) => {
    if (!firebaseUser) return;

    const newValue = !notificationPrefs[key];

    // Update local state immediately for responsive UI
    setNotificationPrefs(prev => ({ ...prev, [key]: newValue }));

    setSavingNotifications(true);
    try {
      // Update Firestore
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        [key]: newValue,
        updatedAt: new Date(),
      });

      toast.success(t('profilePage.preferenceUpdated'));
    } catch (error) {
      console.error('Error updating notification preference:', error);
      toast.error(t('profilePage.preferenceError'));

      // Revert on error
      setNotificationPrefs(prev => ({ ...prev, [key]: !newValue }));
    } finally {
      setSavingNotifications(false);
    }
  };

  return (
    <div className="bg-surface border border-hairline rounded-xl p-6 mb-6">
      <h2 className="font-sans text-lg font-semibold text-ink mb-4">{t('profilePage.notificationPrefs')}</h2>

      <div className="space-y-4">
        {/* Push Notifications Toggle */}
        <div className="flex items-center justify-between p-4 border border-hairline rounded-lg bg-surface-2">
          <div className="flex-1">
            <p className="font-medium text-ink">{t('profilePage.pushNotifications')}</p>
            <p className="text-sm text-ink-muted">{t('profilePage.pushDescription')}</p>
          </div>
          <button
            onClick={() => handleToggleNotification('enablePushNotifications')}
            disabled={savingNotifications}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-canvas disabled:opacity-50 ${
              notificationPrefs.enablePushNotifications
                ? 'bg-accent'
                : 'bg-surface-3'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                notificationPrefs.enablePushNotifications ? 'translate-x-6 bg-on-accent' : 'translate-x-1 bg-ink-subtle'
              }`}
            />
          </button>
        </div>

        {/* Email Notifications Toggle */}
        <div className="flex items-center justify-between p-4 border border-hairline rounded-lg bg-surface-2">
          <div className="flex-1">
            <p className="font-medium text-ink">{t('profilePage.emailNotifications')}</p>
            <p className="text-sm text-ink-muted">{t('profilePage.emailDescription')}</p>
          </div>
          <button
            onClick={() => handleToggleNotification('enableEmailNotifications')}
            disabled={savingNotifications}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-canvas disabled:opacity-50 ${
              notificationPrefs.enableEmailNotifications
                ? 'bg-accent'
                : 'bg-surface-3'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                notificationPrefs.enableEmailNotifications ? 'translate-x-6 bg-on-accent' : 'translate-x-1 bg-ink-subtle'
              }`}
            />
          </button>
        </div>

        {/* Position Update Notification */}
        <div className="flex items-center justify-between p-4 border border-hairline rounded-lg bg-surface-2">
          <div className="flex-1">
            <p className="font-medium text-ink">{t('profilePage.positionUpdate')}</p>
            <p className="text-sm text-ink-muted">{t('profilePage.positionDescription')}</p>
          </div>
          <button
            onClick={() => handleToggleNotification('notifyOnPositionUpdate')}
            disabled={savingNotifications}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-canvas disabled:opacity-50 ${
              notificationPrefs.notifyOnPositionUpdate
                ? 'bg-accent'
                : 'bg-surface-3'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                notificationPrefs.notifyOnPositionUpdate ? 'translate-x-6 bg-on-accent' : 'translate-x-1 bg-ink-subtle'
              }`}
            />
          </button>
        </div>

        {/* Your Turn Notification */}
        <div className="flex items-center justify-between p-4 border border-hairline rounded-lg bg-surface-2">
          <div className="flex-1">
            <p className="font-medium text-ink">{t('profilePage.yourTurnNotice')}</p>
            <p className="text-sm text-ink-muted">{t('profilePage.yourTurnDescription')}</p>
          </div>
          <button
            onClick={() => handleToggleNotification('notifyOnYourTurn')}
            disabled={savingNotifications}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-canvas disabled:opacity-50 ${
              notificationPrefs.notifyOnYourTurn
                ? 'bg-accent'
                : 'bg-surface-3'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                notificationPrefs.notifyOnYourTurn ? 'translate-x-6 bg-on-accent' : 'translate-x-1 bg-ink-subtle'
              }`}
            />
          </button>
        </div>

        {/* Cancellation Notification */}
        <div className="flex items-center justify-between p-4 border border-hairline rounded-lg bg-surface-2">
          <div className="flex-1">
            <p className="font-medium text-ink">{t('profilePage.cancellationNotice')}</p>
            <p className="text-sm text-ink-muted">{t('profilePage.cancellationDescription')}</p>
          </div>
          <button
            onClick={() => handleToggleNotification('notifyOnCancellation')}
            disabled={savingNotifications}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-canvas disabled:opacity-50 ${
              notificationPrefs.notifyOnCancellation
                ? 'bg-accent'
                : 'bg-surface-3'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                notificationPrefs.notifyOnCancellation ? 'translate-x-6 bg-on-accent' : 'translate-x-1 bg-ink-subtle'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="mt-4 p-4 bg-surface-2 border border-hairline rounded-lg">
        <p className="text-sm text-ink-muted">
          <strong className="text-ink font-medium">{t('common.note')}:</strong> {t('profilePage.notificationTip')}
        </p>
      </div>
    </div>
  );
}
